import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DraggableDialog from '../DraggableDialog/DraggableDialog';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import * as actions from '../../../../store/index';
import { fetchGet } from '../../../../helpers/fetching';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from '@material-ui/core';
import Rating from '@material-ui/lab/Rating';
import animation from '../../../../resources/processing_animation.gif';
import EnhancedTableHead from './Common/EnhancedTableHead';
import { formatEMSDateHour } from '../../../../helpers/formatDate';

const useStyles = makeStyles((theme) => ({
  AccordionDetails: {
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '40vh',
  },

  listHeader: {
    fontSize: theme.fonts.responsiveMedium,
    fontWeight: 'bold',
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.texts.primary,
  },

  chipSmall: {
    margin: '6px',
  },

  helperText: {
    color: '#ff7676',
    fontSize: '0.8rem',
    margin: '1px',
  },

  table_placeholder: {
    color: 'gray',
    padding: '15%',
    border: 'none',
    fontSize: '10px',
    fontStyle: 'italic',
    padding: '14px 10px 10px',
    borderBottom: 'unset',
  },
}));

function UsersStats(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();
  const notifications = useSelector((state) => state.gui.notifications);

  const [userStats, setUserStats] = useState([]);
  const [order, setOrder] = useState('ASC');
  const [orderBy, setOrderBy] = useState('');
  const [loading, setLoading] = useState(true);

  const heads = [
    { key: 'username', label: t('users.name'), align: 'left' },
    { key: 'role', label: t('users.role'), align: 'left' },
    { key: 'total_exams', label: t('users.totalexams'), align: 'center' },
    { key: 'rating', label: t('users.rating'), align: 'center' },
    { key: 'revisions', label: t('users.revisions'), align: 'center' },
    { key: 'notes', label: t('users.notes'), align: 'center' },
    { key: 'login_count', label: t('users.times'), align: 'center' },
    { key: 'last_login', label: t('users.lastlogin'), align: 'center' },
  ];

  const fetchUserStats = useCallback(async () => {
    const response = await fetchGet('user_stats');
    if (response.success) {
      setUserStats(response.data);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchUserStats();
      setLoading(false);
    };
    fetchData();
  }, [fetchUserStats]);

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'DESC'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  const formatRating = (rating) => {
    rating = (rating * 5) / 100;
    return rating;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {loading ? (
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}>
          <img
            style={{ width: '100px', height: '100px', borderRadius: '50%' }}
            src={animation}
            alt='logoanimation'
          />
          <Typography style={{ fontSize: '10px', color: 'gray', fontStyle: 'italic' }}>
            {t('Dashboard.loading')}
          </Typography>
        </div>
      ) : (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-labelledby='stats_tableTitle' size={'medium'} stickyHeader>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                heads={heads}
                rowCount={userStats.length}
              />
              {userStats.length > 0 ? (
                <TableBody>
                  {userStats.sort(getComparator(order, orderBy)).map((row, i) => (
                    <TableRow hover tabIndex={-1} key={`${i}-${row.username}`}>
                      {heads.map((head) => {
                        if (head.key === 'rating')
                          return (
                            <TableCell align={head.align} key={`${row.username}-${head.key}`}>
                              <Rating
                                style={{
                                  color: theme.palette.primary.main,
                                }}
                                size='small'
                                value={formatRating(row[head.key])}
                                readOnly
                                precision={0.25}
                              />
                            </TableCell>
                          );
                        else if (head.key === 'last_login')
                          row[head.key] = row[head.key] ? formatEMSDateHour(row[head.key]) : '--';

                        return (
                          <TableCell align={head.align} key={`${row.username}-${head.key}`}>
                            {row[head.key] || '--'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography className={classes.table_placeholder}>{t('UserPanel.nothing')}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

export default UsersStats;
